import statusService from '../services/statusService';
import axios from 'axios';
import axiosRetry from '../helpers/OurAxiosRetry';

class ConnectivityHeartbeat {
  constructor() {
    this.addObserver = this.addObserver.bind(this);
    this.removeObserver = this.removeObserver.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
    this.checkConnectivity = this.checkConnectivity.bind(this);
    this.notifyObservers = this.notifyObservers.bind(this);
    this.context = null;
    this.observers = [];
    this.interval = null;
    this.offlineIntervalInUse = false;
    this.interceptor = null;
    this.active = false;
    this.pingPromise = null;
    this.pingPromiseTime = null;
  }
  
  addObserver(observer) {
    if (this.observers.indexOf(observer) == -1) {
      this.observers.push(observer);
    }
  }
  
  removeObserver(observer) {
    let indexOfObserver = this.observers.indexOf(observer);
    if (indexOfObserver != -1) {
      delete this.observers[indexOfObserver];
    }
  }
  
  start(context, options) {
    if (this.active) {
      return;
    }
    this.options = Object.assign({ use_ping: true, promise_max_time: 15000, health_check_interval: 5000, health_check_timeout: 5000, offline_check_timeout: 5000, health_check_database: true, health_check_redis: false, health_check_elastic: false, check_online_offline_events: false, check_axios: false, retries: 6 }, options);
    this.context = context;
    this.startCheckInterval(this.offlineIntervalInUse);
    if (this.options.check_online_offline_events) {
      window.addEventListener('online', this.checkConnectivity);
      window.addEventListener('offline', this.checkConnectivity);
    }
    if (this.options.check_axios) {
      if (this.interceptor == null) {
        const self = this;
        axios.interceptors.response.use((response) => {
          this.startCheckInterval(self.context.isOffline);
          return response;
        }, (error) => {
          this.startCheckInterval(self.context.isOffline);
          return Promise.reject(error);
        });
      }
    }
    this.active = true;
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.options && this.options.check_online_offline_events) {
      window.removeEventListener('online', this.checkConnectivity);
      window.removeEventListener('offline', this.checkConnectivity);
    }
    if (this.interceptor) {
      axios.interceptors.response.eject(this.interceptor);
      this.interceptor = null;
    }
    this.active = false;
  }

  checkConnectivity(doNotifyObservers = true) {
    if (!this.active) {
      return Promise.resolve(true);
    }
    // if pingPromise exists check for stale (this should handle the case
    // where the ping promise never resolves... which has been observed and if
    // not handles will result in never coming back online or detect offline)
    if (this.pingPromise && ((Date.now() - this.pingPromiseTime) < this.options.promise_max_time)) {
      return this.pingPromise;
    }
    // NOTE: if we think we are offline we will check more frequently
    // but without any retries...
    const  retryConfig = { retries: (!this.offlineIntervalInUse ? this.options.retries : 0), shouldResetTimeout: true, retryDelay: axiosRetry.exponentialDelay };
    this.pingPromiseTime = Date.now();
   
    this.pingPromise = new Promise(resolve => {
      let service = this.options.use_ping ? statusService.ping : statusService.health;
      service(this.options.health_check_timeout, retryConfig)
        .then(res => {
          if (this.options.use_ping) {
            try {
              if (doNotifyObservers) {
                this.notifyObservers(true);
              }
            } finally {
              resolve(true);
            }
          } else {
            if (res.status != 200
              || (this.options.health_check_database
                && res.data.database && res.data.database.status != 'OK')
              || (this.options.health_check_redis
                && res.data.redis && res.data.redis.status != 'OK')
              || (this.options.health_check_elastic
                && res.data.elasticsearch && res.data.elasticsearch.status != 'OK')) {
              try {
                if (doNotifyObservers) {
                  this.notifyObservers(false);
                }
              } finally {
                resolve(false);
              }
            } else {
              try {
                if (doNotifyObservers) {
                  this.notifyObservers(true);
                }
              } finally {
                resolve(true);
              }
            }
          } 
        })
        .catch(error => {
          // NOTE: we may trap "normal" javascript errors here (presumably
          // from the notifyObservers call...) so try to determine if error was
          // related to the ping request...
          if (error.request) {
            try {
              if (doNotifyObservers) {
                this.notifyObservers(false);
              }
            } finally {
              resolve(false);
            }
          }
        });
    });
    return this.pingPromise
    .then(isOnline => {
      this.startCheckInterval(!isOnline);
      this.pingPromise = null;
      this.pingPromiseTime = null;
      return isOnline;
    })
    // NOTE: would like to use finally but it is not consistently available yet
    .catch((isOnline) => {
      this.pingPromise = null;
      this.pingPromiseTime = null;
      return isOnline;
    });
  }

  notifyObservers(isOnline, force) {
    if (this.active || force) {
      this.observers.forEach(observer => {
        observer(isOnline);
      });
    }
  }

  startCheckInterval(isOffline) {
    if (!this.interval || isOffline != this.offlineIntervalInUse) {
      if (this.interval) {
        clearInterval(this.interval);
      }
      const intervalToUse = isOffline ? this.options.offline_check_timeout : this.options.health_check_interval;
      this.interval = setInterval(this.checkConnectivity, intervalToUse);
      this.offlineIntervalInUse = isOffline;
    }
  }

}

export default new ConnectivityHeartbeat();



// WEBPACK FOOTER //
// ./src/helpers/ConnectivityHeartbeat.js